import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { initializedMsalInstance } from "../firebase"; // Adjust path if needed
import {
  updateInvitedProspects,
  updateInviteCount,
  sendManualEmailInvites, // Add new import
} from "../networking/InviteCalls";
import { useAuth } from "../context/AuthContext";
import multiDeviceImg from "../MULTIDEVICE.webp";
import { getUserProfile } from "../networking/DashCalls";

// Helper to fetch the user's display name from Firestore
const fetchFirestoreName = async (uid) => {
    try {
        const profile = await getUserProfile(uid);
        return profile?.name || profile?.displayName || null;
    } catch (err) {
        console.error("[INVITE] Error fetching Firestore name:", err);
        return null;
    }
};

const Invite = () => {
  const [outlookOpen, setOutlookOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [emails, setEmails] = useState(["", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [msalInstance, setMsalInstance] = useState(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [senderName, setSenderName] = useState("");

  // Load the sender name from context or Firestore once on mount
  useEffect(() => {
    const loadName = async () => {
      if (!currentUser) return;
      let name = currentUser.name || currentUser.displayName;
      if (!name && currentUser.uid) {
        name = await fetchFirestoreName(currentUser.uid);
      }
      setSenderName(name || currentUser.email || "");
    };
    loadName();
  }, [currentUser]);

  useEffect(() => {
    initializedMsalInstance
      .then((instance) => setMsalInstance(instance))
      .catch((err) => {
        setError("Failed to initialize MSAL: " + err.message);
        console.error("MSAL initialization error:", err);
      });
  }, []);

  // Validate email format
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Check if all three emails are valid and unique
  const isInviteNowEnabled = () => {
    const filledEmails = emails.filter((email) => email.trim() !== "");
    const uniqueEmails = new Set(filledEmails);
    return (
      filledEmails.length === 3 &&
      uniqueEmails.size === 3 &&
      filledEmails.every(isValidEmail)
    );
  };

  // Handle email input changes
  const handleEmailChange = (index) => (event) => {
    const newEmails = [...emails];
    newEmails[index] = event.target.value;
    setEmails(newEmails);
  };

  // Fetch Outlook contacts
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const accounts = msalInstance.getAllAccounts();
      let accessToken;

      if (accounts.length === 0) {
        const loginResponse = await msalInstance.loginPopup({
          scopes: ["Contacts.Read", "Mail.Send"],
          prompt: "select_account",
        });
        accessToken = loginResponse.accessToken;
      } else {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: ["Contacts.Read", "Mail.Send"],
          account: accounts[0],
        });
        accessToken = tokenResponse.accessToken;
      }

      const response = await fetch(
        "https://graph.microsoft.com/v1.0/me/contacts?$top=100",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch contacts");
      const data = await response.json();
      const contactsWithEmail = data.value.filter(
        (contact) => contact.emailAddresses && contact.emailAddresses.length > 0
      );
      setContacts(contactsWithEmail);
    } catch (err) {
      if (
        err.name === "BrowserAuthError" &&
        err.errorCode === "user_cancelled"
      ) {
        setError("Authentication cancelled. Please try again.");
      } else {
        setError(err.message);
      }
      console.error("Error fetching contacts:", err);
    } finally {
      setLoading(false);
    }
  }, [msalInstance]);

  const handleCheckboxChange = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleOutlookInviteNow = async () => {
    setLoading(true);
    setError(null);
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ["Mail.Send"],
        account: msalInstance.getAllAccounts()[0],
      });
      const accessToken = tokenResponse.accessToken;

      const userEmail = currentUser?.email || "me@example.com";

      // senderName is preloaded from context or Firestore
      const nameToUse = senderName || userEmail;
      console.log(`[INVITE] Sending invites as: ${nameToUse}`);
      const selected = selectedContacts.map((id) =>
          contacts.find((c) => c.id === id)
      );
      const emailPromises = selected.map((contact) => {
          // Compose the HTML email content
          const htmlContent = `<!DOCTYPE html>
  <html>
  <head>
    <title>Quantum Invitation</title>
  </head>
  <body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #ffffff; color: #000000; text-align: center; margin: 0; padding: 0;">
    <img src="https://firebasestorage.googleapis.com/v0/b/quantum-6c796.appspot.com/o/MULTIDEVICE.webp?alt=media&token=c5f811bb-bfa6-43ed-aac3-87d72752f41c"
         alt="Banner" style="width: 100%; display: block; max-width: 600px; margin: 0 auto;">
    <h2 style="font-size: 24px; margin: 30px 0 10px 0; font-weight: bold;">
      ${nameToUse} is using Quantum.
    </h2>
    <div style="font-size: 20px; margin-bottom: 10px; line-height: 1.4;">
      Your Next Big Contract Could Be One Click Away.<br>
      <span style="font-size: 17px; margin-top: 6px; display: block;">
        Sign-up for our daily updates email today!
      </span>
    </div>
    <div style="padding: 18px 0 8px 0;">
      <a href="https://welcome.quantum-crm.com/"
         style="display: inline-block; background: transparent; color: #00274D; text-decoration: none; font-weight: bold; font-size: 18px; border-radius: 6px; padding: 12px 32px; border: 2px solid #00274D; margin-bottom: 6px;">
        Sign Up for Free
      </a>
    </div>
  </body>
  </html>`;

          const email = {
              message: {
                  // Personalize the subject line with the sender name
                  subject: `${nameToUse} invited you to try Quantum!`,
                  body: {
                      contentType: "HTML",
                      content: htmlContent,
                  },
                  from: {
                      emailAddress: {
                          address: userEmail,
                          name: nameToUse,
                      },
                  },
                  toRecipients: [
                      {
                          emailAddress: {
                              address: contact.emailAddresses[0].address,
                          },
                      },
                  ],
              },
              saveToSentItems: true,
          };

        return fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(email),
        }).then(async (response) => {
          if (!response.ok) {
            throw new Error(
              `Failed to send email to ${contact.emailAddresses[0].address}`
            );
          }
          console.log(`Invite sent to ${contact.emailAddresses[0].address}`);
          await updateInvitedProspects(
            contact.emailAddresses[0].address,
            userEmail
          );
        });
      });

      await Promise.all(emailPromises);
      console.log("Invites sent to:", selected);
      await updateInviteCount(selected.length);
      setOutlookOpen(false);
      navigate("/");
    } catch (err) {
      setError(err.message);
      console.error("Error sending invites:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleManualInviteNow = async () => {
    setLoading(true);
    setError(null);
    try {
      const userEmail = currentUser?.email || "me@example.com";
      const nameToUse = senderName || userEmail;
      console.log(`[INVITE] Manual invite from: ${nameToUse}`);

      // Call the Cloud Function to send emails
      await sendManualEmailInvites(emails, nameToUse);

      // Update invited prospects for each email
      const prospectPromises = emails.map((emailAddress) =>
        updateInvitedProspects(emailAddress, userEmail)
      );
      await Promise.all(prospectPromises);

      // Update invite count
      await updateInviteCount(emails.length); // Should be 3

      console.log("Manual invites processed:", emails);
      setManualOpen(false);
      navigate("/");
    } catch (err) {
      setError(err.message);
      console.error("Error processing manual invites:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setError(null);
    setContacts([]);
    setSelectedContacts([]);
    fetchContacts();
  };

  const handleOutlookClose = () => setOutlookOpen(false);
  const handleManualClose = () => setManualOpen(false);

  return (
    <>
      <CssBaseline />
      <Grid container sx={{ minHeight: "100vh" }}>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box sx={{ p: 4 }}>
            <img
              src={multiDeviceImg}
              alt="Multi Device"
              style={{ width: "100%", maxWidth: "100%" }}
            />
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 4,
          }}
        >
          <Typography variant="h2" gutterBottom>
            We love it when you share!
          </Typography>
          <Typography variant="h5" gutterBottom>
            Invite three or more people to maintain the free tier.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setOutlookOpen(true);
              fetchContacts();
            }}
            sx={{ m: 1, width: 300 }}
          >
            Invite from Outlook Contacts
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setManualOpen(true)}
            sx={{ m: 1, width: 300 }}
          >
            Manually Invite by Email
          </Button>
        </Grid>
      </Grid>

      {/* Outlook Contacts Popup */}
      <Dialog
        open={outlookOpen}
        onClose={handleOutlookClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invite Contacts from Outlook</DialogTitle>
        <DialogContent sx={{ maxHeight: "400px", overflowY: "auto" }}>
          {loading && <Typography>Loading contacts...</Typography>}
          {error && <Typography color="error">Error: {error}</Typography>}
          {!loading && !error && contacts.length === 0 && (
            <Typography>No contacts found with email addresses.</Typography>
          )}
          <List>
            {contacts.map((contact) => (
              <ListItem key={contact.id}>
                <Checkbox
                  checked={selectedContacts.includes(contact.id)}
                  onChange={() => handleCheckboxChange(contact.id)}
                />
                <ListItemText
                  primary={contact.displayName || "No Name"}
                  secondary={contact.emailAddresses[0]?.address || "No Email"}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions
          sx={{ position: "sticky", bottom: 0, bgcolor: "background.paper" }}
        >
          {error && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleTryAgain}
              sx={{ mr: 1 }}
            >
              Try Again
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleOutlookInviteNow}
            disabled={
              selectedContacts.length < 3 || loading || !msalInstance || error
            }
          >
            Invite Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Invite Popup */}
      <Dialog
        open={manualOpen}
        onClose={handleManualClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manually Invite by Email</DialogTitle>
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              Error: {error}
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Email 1"
              value={emails[0]}
              onChange={handleEmailChange(0)}
              fullWidth
              error={emails[0] && !isValidEmail(emails[0])}
              helperText={
                emails[0] && !isValidEmail(emails[0]) ? "Invalid email" : ""
              }
            />
            <TextField
              label="Email 2"
              value={emails[1]}
              onChange={handleEmailChange(1)}
              fullWidth
              error={emails[1] && !isValidEmail(emails[1])}
              helperText={
                emails[1] && !isValidEmail(emails[1]) ? "Invalid email" : ""
              }
            />
            <TextField
              label="Email 3"
              value={emails[2]}
              onChange={handleEmailChange(2)}
              fullWidth
              error={emails[2] && !isValidEmail(emails[2])}
              helperText={
                emails[2] && !isValidEmail(emails[2]) ? "Invalid email" : ""
              }
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ position: "sticky", bottom: 0, bgcolor: "background.paper" }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleManualInviteNow}
            disabled={!isInviteNowEnabled() || loading}
          >
            Invite Now
          </Button>
        </DialogActions>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default Invite;
